import React, { useState, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { HeaderNavigation } from '@/components/HeaderNavigation'
import { TransactionsList } from '@/components/TransactionsList'
import { ActivityFilters } from '@/components/ActivityFilters'
import { PullToRefresh } from '@/components/PullToRefresh'
import { AppIcons } from '@/components/AppIcons'
import { EmptyTransaction } from '@/components/EmptyStates'
import { TransactionTypes, RangeDate } from '@/domain/transactions'
import { useTheme } from '@/hooks/useTheme'
import { useUserTransactions } from '@/hooks/useUserTransactions'
import { usePullToRefreshHandler } from '@/hooks'

interface UserTransactionsParams {
  userId?: string
  fullName?: string
  types?: string
}

export const UserTransactionsScreen: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showBackToTop, setShowBackToTop] = useState(false)

  // Get params from location state or URL search params
  const locationState = location.state as UserTransactionsParams | null
  const searchParams = new URLSearchParams(location.search)

  const userId = locationState?.userId || searchParams.get('userId') || ''
  const fullName = locationState?.fullName || searchParams.get('fullName') || ''
  const typesParam = locationState?.types || searchParams.get('types') || ''

  // Parse types if provided
  const initialTypes = typesParam
    ? (typesParam.split(',') as TransactionTypes[])
    : []

  // Filter states
  const [searchValue, setSearchValue] = useState('')
  const [selectedTypes, setSelectedTypes] =
    useState<TransactionTypes[]>(initialTypes)
  const [dateRange, setDateRange] = useState<RangeDate>({
    startDate: null,
    endDate: null,
  })

  // Determine if filters are active
  const hasActiveFilters = Boolean(
    selectedTypes.length > 0 || (dateRange.startDate && dateRange.endDate),
  )

  // Dynamic padding depending on filters
  const dynamicPaddingTop = hasActiveFilters ? '210px' : '152px'

  // Fetch user transactions
  const {
    transactions,
    transactionsQuery,
    isLoading,
    isRefetching,
    isError,
    refetch,
  } = useUserTransactions({
    userId,
    searchValue,
    selectedTypes,
    dateRange,
  })

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = transactionsQuery

  // Handle scroll for back to top button
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = event.currentTarget.scrollTop
      const shouldShow = scrollTop > 300

      if (shouldShow !== showBackToTop) {
        setShowBackToTop(shouldShow)
      }
    },
    [showBackToTop],
  )

  // Handle back to top
  const handleBackToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Handle filter changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  const handleTypesChange = useCallback((types: TransactionTypes[]) => {
    setSelectedTypes(types)
  }, [])

  const handleDateRangeChange = useCallback((range: RangeDate) => {
    setDateRange(range)
  }, [])

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchValue('')
    setSelectedTypes([])
    setDateRange({ startDate: null, endDate: null })
  }, [])

  const { isRefreshing, handlePullRefresh } = usePullToRefreshHandler(refetch, {
    showErrorToast: true,
  })

  if (!userId) {
    return (
      <BaseSafeFormView flex={1} bgColor="background.screen">
        <HeaderNavigation title={t('screens:userTransactions.title')} />
        <BaseView flex={1} center px={20}>
          <BaseText
            fontSize={18}
            fontFamily="medium"
            color="label.default"
            textAlign="center"
            className="mb-4"
          >
            {t('common:userNotFound')}
          </BaseText>
          <BaseText
            fontSize={14}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
          >
            {t('screens:userTransactions.userTransactionsNotAvailable')}
          </BaseText>
        </BaseView>
      </BaseSafeFormView>
    )
  }

  return (
    <PullToRefresh
      onRefresh={handlePullRefresh}
      isRefreshing={isRefreshing}
      debug={process.env.NODE_ENV === 'development'}
    >
      <BaseSafeFormView flex={1} bgColor="background.screen">
        {/* Fixed Header and Filters */}
        <BaseView
          style={{
            position: 'fixed',
            // top: '60px', // After the header
            left: 13,
            right: 13,
            zIndex: 10,
            backgroundColor: theme.colors.background.screen,
            // borderBottom: `1px solid ${theme.colors.background.cardItemRoot}`,
          }}
        >
          <HeaderNavigation
            title={fullName || t('screens:userTransactions.title')}
            onBackPress={() => navigate(-1)}
          />
          <ActivityFilters
            searchValue={searchValue}
            selectedTypes={selectedTypes}
            dateRange={dateRange}
            onSearchChange={handleSearchChange}
            onTypesChange={handleTypesChange}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </BaseView>

        {/* Scrollable Content */}
        <BaseView
          flex={1}
          style={{
            paddingTop: dynamicPaddingTop,
            position: 'relative',
          }}
        >
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="content-scroll"
            style={{
              height: '100%',
              overflow: 'auto',
              paddingBottom: '100px',
            }}
          >
            {/* Transactions List or Loading/Error State */}
            {transactions.length === 0 && isLoading ? (
              <BaseView px={12}>
                <EmptyTransaction isLoadingTransactions={true} />
              </BaseView>
            ) : transactions.length === 0 && isError && !isLoading ? (
              <BaseView center py={40} px={20}>
                <BaseText
                  fontSize={14}
                  fontFamily="regular"
                  color="label.default"
                  textAlign="center"
                  style={{ opacity: 0.7 }}
                >
                  {t('screens:userTransactions.transactionsNotFound')}
                </BaseText>
              </BaseView>
            ) : transactions.length === 0 && !isLoading ? (
              <BaseView center py={40} px={20}>
                <BaseView align="center" gap={16}>
                  <BaseText fontSize={48} style={{ opacity: 0.5 }}>
                    📝
                  </BaseText>

                  <BaseView align="center" gap={8}>
                    <BaseText
                      fontSize={18}
                      fontFamily="semibold"
                      color="label.default"
                      textAlign="center"
                    >
                      {t('screens:userTransactions.noTransactionsWithUser', {
                        name: fullName,
                      })}
                    </BaseText>

                    <BaseText
                      fontSize={16}
                      fontFamily="regular"
                      color="label.description"
                      textAlign="center"
                      style={{ maxWidth: '280px' }}
                    >
                      {t('screens:userTransactions.startTransactionWithUser', {
                        name: fullName,
                      })}
                    </BaseText>
                  </BaseView>
                </BaseView>
              </BaseView>
            ) : (
              <TransactionsList
                transactions={transactions}
                isLoading={isLoading && transactions.length === 0}
                onLoadMore={handleLoadMore}
                hasNextPage={hasNextPage}
                isLoadingMore={isFetchingNextPage}
                itemOffsetX={0}
              />
            )}
          </div>
        </BaseView>

        {/* Back to Top Button */}
        {showBackToTop && (
          <BaseView
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 20,
            }}
          >
            <BaseView
              center
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '24px',
                backgroundColor: theme.colors.background.semiTransparent,
                backdropFilter: 'blur(10px)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: `1px solid ${theme.colors.background.cardItemRoot}`,
              }}
              onClick={handleBackToTop}
            >
              <AppIcons
                name="doubleChevronUp"
                size={24}
                color={theme.colors.icon.default}
              />
            </BaseView>
          </BaseView>
        )}
      </BaseSafeFormView>
    </PullToRefresh>
  )
}
